import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";

export function Wordmark({ className = "" }: { className?: string }) {
  return (
    <Link to="/" className={`wordmark text-2xl text-text no-underline ${className}`} title={__APP_VERSION__}>
      .MAYUSCULANOTE
    </Link>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
  loading?: boolean;
};
export function Button({
  variant = "primary",
  loading,
  className = "",
  children,
  disabled,
  ...rest
}: BtnProps) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-sm px-4 py-2.5 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed";
  const v = {
    primary: "bg-accent text-bg hover:bg-[#e6e6e6]",
    ghost: "bg-transparent text-text border border-border hover:bg-panel-2",
    danger: "bg-transparent text-error border border-error/40 hover:bg-error/10",
  }[variant];
  return (
    <button className={`${base} ${v} ${className}`} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      aria-hidden
    />
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | null };
export function Field({ label, error, id, className = "", ...rest }: FieldProps) {
  const fid = id ?? rest.name ?? label;
  return (
    <label htmlFor={fid} className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-semibold text-text">{label}</span>
      <input
        id={fid}
        className="w-full rounded-sm border border-border bg-panel-2 px-3 py-2.5 text-text placeholder:text-dim outline-none focus:border-accent"
        {...rest}
      />
      {error ? <span className="mt-1 block text-sm text-error">{error}</span> : null}
    </label>
  );
}

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-md border border-border bg-panel p-6 shadow-panel ${className}`}>{children}</div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="display mb-3 border-b border-border pb-2 text-2xl">.{children}</h2>;
}

export function Alert({
  kind = "info",
  children,
}: {
  kind?: "info" | "error" | "success";
  children: ReactNode;
}) {
  const c = {
    info: "border-border text-muted",
    error: "border-error/50 text-error",
    success: "border-success/50 text-success",
  }[kind];
  return <div className={`rounded-sm border bg-panel-2 px-3 py-2 text-sm ${c}`}>{children}</div>;
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: string }[];
}) {
  return (
    <div className="mb-5 grid grid-flow-col auto-cols-fr overflow-hidden rounded-sm border border-border">
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          className={`px-3 py-2.5 text-sm font-semibold transition ${value === it.value ? "bg-accent text-bg" : "bg-panel-2 text-muted hover:text-text"}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
